import { useCallback, useEffect } from "react";

import { Icon } from "../icon/Icon";
import { Popover } from "../popover/Popover";
import { PLAYBACK_RATE_OPTIONS } from "./constants";
import { Text } from "../text/Text";
import { Box } from "../box/Box";
import { Button } from "../button/Button";

import type { Placement, Strategy } from "@floating-ui/react";

export interface PlaybackRateControlProps {
  className?: string;
  popoverStrategy?: Strategy;
  popoverPlacement?: Placement;
  popoverOffset?: number;
  value?: number;
  onChange?(value: number): void;
  onActivate?: () => void;
}

interface PlaybackRateOptionsProps {
  close?(): void;
  onActivate?(): void;
  onChange?(rate: number): void;
}

const PlaybackRateOptions = ({
  close,
  onActivate,
  onChange,
}: PlaybackRateOptionsProps) => {
  useEffect(() => {
    onActivate?.();
  }, [onActivate]);

  const handleClick = useCallback(
    (value: number) => {
      return () => {
        onChange?.(value);
        close?.();
      };
    },
    [close, onChange],
  );

  return (
    <Box direction="column" align="start" gap={2}>
      {PLAYBACK_RATE_OPTIONS.map((rate) => (
        <Button
          key={rate}
          appearance="minimal"
          size="xs"
          fill
          onClick={handleClick(rate)}
        >
          {`${rate}x`}
        </Button>
      ))}
    </Box>
  );
};

export const PlaybackRateControl = ({
  popoverPlacement,
  popoverOffset,
  popoverStrategy,
  value,
  className,
  onChange,
  onActivate,
}: PlaybackRateControlProps) => {
  return (
    <Popover
      strategy={popoverStrategy}
      placement={popoverPlacement}
      offset={popoverOffset}
      elevation={2}
    >
      <Popover.Trigger>
        <button className={className}>
          <Icon size="lg" icon="gauge" variant="regular" />
          <Text size="sm" weight="semibold">
            {`${value}x`}
          </Text>
        </button>
      </Popover.Trigger>

      <Popover.Panel>
        {({ close }) => {
          return (
            <PlaybackRateOptions
              close={close}
              onChange={onChange}
              onActivate={onActivate}
            />
          );
        }}
      </Popover.Panel>
    </Popover>
  );
};
